import type { PlanType } from '../../../lib/config/pricing'
// Type-only: erased at build time, so this costs the frontend bundle nothing
// while making a model rename a compile error rather than a silent free upgrade.
import type { ImageModel } from '../../ai/functions/generateImage'

export const THIRTY_DAYS_MS = 30 * 24 * 60 * 60 * 1000

type PeriodLimit = 'post' | 'image' | 'chat'
type EntityLimit = 'moodboards' | 'brands' | 'automations'
/** Not a count — a capability the tier either has or doesn't. */
type CapabilityLimit = 'model' | 'proModel' | 'api' | 'mcp'

/** Everything `TIER_LIMITS` can put a number on. */
export type NumericLimit = PeriodLimit | EntityLimit
export type LimitType = NumericLimit | CapabilityLimit

export const LIMIT_LABELS: Record<LimitType, string> = {
  post: 'Posts',
  image: 'Image Generations',
  chat: 'Chat Messages',
  moodboards: 'Moodboards',
  brands: 'Brands',
  automations: 'Automations',
  model: 'Premium Models',
  proModel: 'Pro Models',
  api: 'API Access',
  mcp: 'MCP Access',
}

/** Image models the free tier may use. Everything else in `IMAGE_MODELS`
 *  requires Standard.
 *
 *  This is the *enforced* copy. The picker mirrors it to grey out the paid
 *  options, but the picker is only one of three ways in: the v1 REST API and
 *  MCP both reach image generation through the same tRPC route, and neither
 *  renders a dropdown. Keep the check server-side or it isn't a check. */
export const FREE_TIER_IMAGE_MODELS: readonly ImageModel[] = [
  'flux-2-pro',
  'gemini-2.5-flash-image',
  // The provider aliases resolve to the two above; the workspace chat sends them.
  'flux',
  'gemini',
]

/** The top-rated, most expensive image models: Pro-level tiers only (see
 *  `TIER_HAS_PRO_IMAGE_MODELS`). Same enforcement rule as the free list:
 *  the picker only mirrors it. */
export const PRO_TIER_IMAGE_MODELS: readonly ImageModel[] = [
  'gpt-image-2.5-sunburst',
  'gpt-image-2.5-flare',
  'mai-image-2.6',
]

const INF = Infinity

/**
 * Single source of truth for every limit on every tier.
 * `Infinity` means unlimited.
 *
 * Free/Standard/Pro mirror the published pricing page (getcassi.ai/pricing);
 * change them together or the page lies.
 */
export const TIER_LIMITS: Record<PlanType, Record<NumericLimit, number>> = {
  free: { post: 5, image: 25, chat: 100, moodboards: 1, brands: 1, automations: 0 },
  standard: { post: INF, image: 500, chat: 1000, moodboards: INF, brands: 3, automations: 10 },
  pro: { post: INF, image: 2000, chat: INF, moodboards: INF, brands: 10, automations: 50 },
  // Pro limits for the trial, except images: 2,000 free generations in 14
  // days is real money, so the trial keeps the old 500 cap.
  'pro-trial': { post: INF, image: 500, chat: INF, moodboards: INF, brands: 10, automations: 50 },
  enterprise: { post: INF, image: INF, chat: INF, moodboards: INF, brands: INF, automations: INF },
  // AppSumo lifetime tiers, mirroring the deal page. "Auto-designs" on the
  // deal page is the shared `image` counter — manual generations draw from
  // the same pool. "Unlimited: fair usage" is Infinity here; fair usage is
  // deal-page policy, not something we meter. Automations were not part of
  // the deal, so they ladder alongside the paid tiers (T1≈Standard, T3≈Pro).
  'appsumo-tier-1': {
    post: 25,
    image: 300,
    chat: 500,
    moodboards: INF,
    brands: 1,
    automations: 10,
  },
  'appsumo-tier-2': {
    post: 100,
    image: 1500,
    chat: 5000,
    moodboards: INF,
    brands: 3,
    automations: 25,
  },
  'appsumo-tier-3': {
    post: 300,
    image: INF,
    chat: 20000,
    moodboards: INF,
    brands: INF,
    automations: 50,
  },
}

/**
 * Enforced ceilings on limits that `TIER_LIMITS` advertises as unlimited.
 *
 * AppSumo tier 3 sells "unlimited auto-designs (fair usage)". Every generation
 * costs real model spend, so one heavy account could burn through it with no
 * marginal cost to them. The ceiling is enforced but never displayed: the
 * usage query keeps reporting unlimited, because that is what the deal page
 * promised and what the customer bought.
 */
export const FAIR_USE_CAPS: Partial<Record<PlanType, Partial<Record<PeriodLimit, number>>>> = {
  'appsumo-tier-3': { image: 3000 },
}

/**
 * Which tiers may use the v1 REST API (key creation and key auth both check
 * this). Standard deliberately has no API access — the API is a Pro selling
 * point — but AppSumo tier 3 keeps it, per the locked-in deal page.
 */
export const TIER_HAS_API_ACCESS: Record<PlanType, boolean> = {
  free: false,
  standard: false,
  pro: true,
  'pro-trial': true,
  enterprise: true,
  'appsumo-tier-1': false,
  'appsumo-tier-2': false,
  'appsumo-tier-3': true,
}

/**
 * Which tiers may use `PRO_TIER_IMAGE_MODELS`. Follows API access: the trial
 * gets them so it shows what Pro is, and AppSumo tier 3 is the Pro-equivalent
 * lifetime plan.
 */
export const TIER_HAS_PRO_IMAGE_MODELS: Record<PlanType, boolean> = {
  free: false,
  standard: false,
  pro: true,
  'pro-trial': true,
  enterprise: true,
  'appsumo-tier-1': false,
  'appsumo-tier-2': false,
  'appsumo-tier-3': true,
}

/**
 * Which tiers may use MCP (OAuth-token auth, checked per request in the API
 * service's bearer lookup). Every paid tier has it; only free is excluded.
 * All three AppSumo tiers must stay `true` — the deal page promises MCP.
 */
export const TIER_HAS_MCP_ACCESS: Record<PlanType, boolean> = {
  free: false,
  standard: true,
  pro: true,
  'pro-trial': true,
  enterprise: true,
  'appsumo-tier-1': true,
  'appsumo-tier-2': true,
  'appsumo-tier-3': true,
}
